/**
 * The HTTP contract, as the client sees it.
 *
 * This mirrors what the Django backend serializes in
 * `backend/prompts_app/api/serialization.py`. Because the two sides are written
 * in different languages, the shape is pinned by a test on each side:
 *  - backend: `tests/api/test_contract.py`
 *  - client:  `test/web/contract.test.ts`
 * Changing one without the other fails a build.
 */

export type SubmissionResponse = {
  readonly id: string;
  readonly prompt: string;
  readonly response: string;
  /** ISO 8601, UTC, millisecond precision, `Z` suffix. */
  readonly createdAt: string;
};

export type ContactRequestPayload = {
  readonly name: string;
  readonly company: string;
  readonly email: string;
  /** Free-text note. Optional. */
  readonly note: string;
};

export type ContactResponse = ContactRequestPayload & {
  readonly id: string;
  /** ISO 8601, UTC, millisecond precision, `Z` suffix. */
  readonly createdAt: string;
};

/** Fields the contact form sends. Kept as a value so a test can assert on them. */
export const CONTACT_FIELDS = ["name", "company", "email", "note"] as const;

export type ContactField = (typeof CONTACT_FIELDS)[number];

export type ErrorResponse = {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly details?: Readonly<Record<string, string>>;
  };
};

/** Keys the client relies on. Kept as a value so a test can assert on them. */
export const SUBMISSION_FIELDS = [
  "id",
  "prompt",
  "response",
  "createdAt",
] as const;

export type SessionUser = {
  readonly email: string;
  readonly name: string;
  /** Avatar URL. May be empty. */
  readonly picture: string;
};

/** `user` is null when signed out — the ordinary first-load case, not an error. */
export type SessionResponse = {
  readonly user: SessionUser | null;
};

/**
 * Kept as a value so a test can assert on them.
 *
 * Google's `sub` is deliberately absent: the server never sends it, so the
 * client cannot come to depend on a stable cross-site identifier.
 */
export const SESSION_USER_FIELDS = ["email", "name", "picture"] as const;

/** One line of the admin activity log. */
export type InteractionEvent = {
  readonly id: string;
  /** Opaque position marker; pass back as `before` for the next page. */
  readonly cursor: string;
  readonly kind: string;
  /** ISO 8601, UTC, millisecond precision, `Z` suffix. */
  readonly occurredAt: string;
  readonly actorEmail: string;
  readonly subjectId: string;
  /** The user's text, already truncated by the server. Untrusted. */
  readonly summary: string;
  readonly ipAddress: string;
  readonly userAgent: string;
  /** The full text the event received. Untrusted. May be empty. */
  readonly input: string;
  /** The full text the event produced. Untrusted. May be empty. */
  readonly output: string;
};

export type InteractionPage = {
  readonly events: readonly InteractionEvent[];
  /** `null` on the last page. */
  readonly nextCursor: string | null;
  readonly limit: number;
};

/**
 * Kept as values so a test can assert on them.
 *
 * `referer` and `acceptLanguage` are deliberately absent: the server records
 * them but returns them to nobody, including administrators.
 */
export const INTERACTION_EVENT_FIELDS = [
  "id",
  "cursor",
  "kind",
  "occurredAt",
  "actorEmail",
  "subjectId",
  "summary",
  "ipAddress",
  "userAgent",
  "input",
  "output",
] as const;

export const INTERACTION_PAGE_FIELDS = ["events", "nextCursor", "limit"] as const;

/** One blank-line-separated section of an attributed prompt. */
export type AttributionSection = {
  readonly index: number;
  /** The section's own text, echoed back. Untrusted. */
  readonly text: string;
  /** Shapley share of the output length, in tokens. May be negative. */
  readonly shapley: number;
  /** Change in output tokens when only this section is removed. */
  readonly leaveOneOut: number;
};

export type AttributionResponse = {
  readonly model: string;
  readonly totalRuns: number;
  readonly baselineOutputTokens: number;
  /** The model's actual response to the full prompt (the baseline run). */
  readonly baselineOutput: string;
  readonly sections: readonly AttributionSection[];
};

/** Kept as values so a test can assert on them. */
export const ATTRIBUTION_SECTION_FIELDS = [
  "index",
  "text",
  "shapley",
  "leaveOneOut",
] as const;

export const ATTRIBUTION_RESPONSE_FIELDS = [
  "model",
  "totalRuns",
  "baselineOutputTokens",
  "baselineOutput",
  "sections",
] as const;
